import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import MiniCart from './Components/MiniCart';
import QuickSearch from './Components/QuickSearch';
import Navigation from './Components/Navigation';
import FacetedSearchContainer from './Components/FacetedSearchContainer';
import DynamicComponent from './Components/DynamicComponent';
import { initializeSwiper } from './swiper';
import QuickSearchButton from "./Components/QuickSearchButton";

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
  app,
  preloadState,
  composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
  if (!store.asyncReducers[key]) {
    store.asyncReducers[key] = asyncReducer;
    store.replaceReducer(createReducer(store.asyncReducers));
  }
};

window.__litium = {
  ...window.__litium,
  bootstrapComponents: () => {
    // bootstrap React components, in case the HTML response we receive from the server
    // has React components. ReactDOM.render performs only an update on previous rendered
    // components and only mutate the DOM as necessary to reflect latest React element.
    bootstrapComponents();
  },
  cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
  registeredContainers.push(container);
  ReactDOM.render(element, container, callback);
};

window.onunload = () => {
  // make sure components are unmounted, redux's listener are unsubscribed
  // to fix issue with iframe navigation in IE
  registeredContainers.forEach((container) => {
    ReactDOM.unmountComponentAtNode(container);
  });
};


function initializeComponentsByClassName(className, component) {
    Array.from(document.getElementsByClassName(className)).forEach(item => {
        renderReact(
            <Provider store={store}>
                {component}
            </Provider>,
            item
        );
    });
}

window.scrollToElement = function (selector){
    const element = document.querySelector(selector);
    
    window.scroll({
        behavior: 'smooth',
        left: 0,
        top: element.offsetTop
    });
}

const bootstrapComponents = () => {
 
  if (document.getElementById('quickSearch')) {
    renderReact(
      <Provider store={store}>
        <QuickSearch />
      </Provider>,
      document.getElementById('quickSearch')
    );
  }

  if (document.getElementById('quickSearchButton')) {
    const QuickSearchButton = DynamicComponent({
      loader: () => import('./Components/QuickSearchButton'),
    });
  
    renderReact(
      <Provider store={store}>
        <QuickSearchButton/>
      </Provider>,
      document.getElementById('quickSearchButton')
    );
  }
  if (document.getElementById('navbar')) {
    renderReact(
      <Provider store={store}>
        <Navigation />
      </Provider>,
      document.getElementById('navbar')
    );
  }
  if (document.getElementById('facetedSearchCompact')) {
    renderReact(
      <Provider store={store}>
        <FacetedSearchContainer />
      </Provider>,
      document.getElementById('facetedSearchCompact')
    );
  }
  if (document.getElementById('myPagePersons')) {
    const PersonList = DynamicComponent({
      loader: () => import('./Components/PersonListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <PersonList />
      </Provider>,
      document.getElementById('myPagePersons')
    );
  }
  if (document.getElementById('myPageAddresses')) {
    const AddressList = DynamicComponent({
      loader: () => import('./Components/AddressListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <AddressList />
      </Provider>,
      document.getElementById('myPageAddresses')
    );
  }
  if (document.getElementById('checkout')) {
    const Checkout = DynamicComponent({
      loader: () => import('./Components/Checkout'),
    });
    renderReact(
      <Provider store={store}>
        <Checkout />
      </Provider>,
      document.getElementById('checkout')
    );
  }
  if (document.getElementById('lightBoxImages')) {
    const LightboxImages = DynamicComponent({
      loader: () => import('./Components/LightboxImages'),
    });
    import('./Reducers/LightboxImages.reducer').then(({ lightboxImages }) => {
      store.injectReducer('lightboxImages', lightboxImages);
      const rootElement = document.getElementById('lightBoxImages');
      const images = Array.from(rootElement.querySelectorAll('template')).map(
        (img) => ({
          html: img.innerHTML,
          src: img.dataset.src,
        })
      );
      renderReact(
        <Provider store={store}>
          <LightboxImages images={images} />
        </Provider>,
        document.getElementById('lightBoxImages')
      );
    });
  }

  if (document.querySelectorAll('.slider').length > 0) {
    const Slider = DynamicComponent({
      loader: () => import('./Components/Slider'),
    });
    Array.from(document.querySelectorAll('.slider')).forEach(
      (slider, index) => {
        const values = [...slider.querySelectorAll('template')].map((block) => {
          return {
            html: block.innerHTML,
          };
        });
        if (values.length > 0) {
          renderReact(<Slider values={values} />, slider);
        }
      }
    );
    if (values.length > 0) {
      renderReact(<Slider values={values} />, slider);
    }
  }

  if (document.querySelectorAll('buy-button').length > 0) {
    const BuyButton = DynamicComponent({
      loader: () => import('./Components/BuyButton'),
    });
    Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
      const {
        articleNumber,
        quantityFieldId,
        includeAccessoryItems,
        href,
        cssClass,
        label,
      } = button.dataset;
      renderReact(
        <Provider store={store}>
          <BuyButton
            {...{
              label,
              articleNumber,
              quantityFieldId,
              includeAccessoryItems,
              href,
              cssClass,
            }}
          />
        </Provider>,
        button
      );
    });
  }
  
  if (document.querySelectorAll('[data-quantity-field]').length > 0) {
    const QuantityField = DynamicComponent({
        loader: () => import('./Components/QuantityField'),
    });
    Array.from(document.querySelectorAll('[data-quantity-field]')).forEach((field) => {
        const { fieldId } = field.dataset;
        renderReact(
            <Provider store={store}>
                <QuantityField id={fieldId}/>
            </Provider>,
            field
        );
    });
  }
  if (document.getElementById('orderHistoryPage')) {
    const OrderList = DynamicComponent({
      loader: () => import('./Components/OrderHistoryListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <OrderList />
      </Provider>,
      document.getElementById('orderHistoryPage')
    );
  }
  
  if (document.querySelectorAll('.accessory-selector__container').length > 0) {
      const AccessorySelector = DynamicComponent({
          loader: () => import('./Components/ProductDropdown/AccessorySelector'),
      });
      
      function parseBooleanString(str) {
          return str?.toLowerCase() === 'true';
      }
      
      Array.from(document.querySelectorAll('.accessory-selector__container'))
          .forEach((selector, groupIndex) => {
              const values = [
                  ...selector.querySelectorAll('.accessory-selector__item'),
              ].map((item) => {
                  const {
                      classes,
                      groupId,
                      itemId,
                      itemName,
                      itemRequiresPlacement,
                      itemSelected,
                      itemImageUrl,
                      itemImageAlt,
                      itemUrl,
                      itemEnabled,
                      itemDefault,
                  } = item.dataset;
                  
                  return {
                      classes, groupIndex, groupId, itemId, itemName,
                      requiresPlacement: parseBooleanString(itemRequiresPlacement),
                      selected: parseBooleanString(itemSelected),
                      image: {
                          url: itemImageUrl,
                          alt: itemImageAlt,
                      },
                      url: itemUrl,
                      isEnabled: parseBooleanString(itemEnabled),
                      isDefault: parseBooleanString(itemDefault),
                  };
              });
                
              if (values.length > 0) {
                  const { title } = selector.dataset;
                  
                  renderReact(
                      <Provider store={store}>
                          <AccessorySelector values={values}
                                             title={title} />
                      </Provider>,
                      selector);
              }
          });
  }

  if (document.getElementById('itemPrice')) {
      const Price = DynamicComponent({
          loader: () => import('./Components/Price'),
      });
      const priceElement = document.getElementById('itemPrice');
      
      const { itemAccessories, itemId } = priceElement.dataset;
      const accessories = itemAccessories
          ? itemAccessories.split(',')
          : null;
      
      renderReact(
          <Provider store={store}>
              <Price 
                  itemId={priceElement.dataset.itemId}
                  accessories={accessories} />
          </Provider>,
          priceElement
      );
  }
  if (document.getElementById('resellersSearch_block')) {
      const ResellersSearch = DynamicComponent({
          loader: () => import('./Components/ResellersSearch/ResellersSearch'),
      });
      renderReact(
          <Provider store={store}>
              <ResellersSearch/>
          </Provider>,
          document.getElementById('resellersSearch_block')
      );
  }

  initializeComponentsByClassName('miniCart', <MiniCart />);
  initializeSwiper();
};

bootstrapComponents();
